<template>
  <AsideGabarit :action="action" :title="$t('filters.targetSitesDisplayed', sortedSites.length)">
    <Draggable v-model="sortedSites" :disabled="!editing" :item-key="element => sortedSites.indexOf(element)" class="site-container" handle=".handle">
      <template #item="{ element, index }">
        <div :class="{ editing: editing }" class="site">
          <div v-if="editing" class="handle">☰</div>

          <ToggleFilter
            :action="() => toggleFilterSites(element.id, index)"
            :baseline="element.address"
            :caption="element.name"
            :override-active="isSiteActive(element.id)"
          >
            <template #icon>
              <img :src="currentPin(index)" alt="" class="icon-pin" />
            </template>
          </ToggleFilter>
        </div>
      </template>
    </Draggable>

    <div v-if="sortedSites.length > 1 && [0, 1].includes(userInfo.role_id)" class="edit-container" @click="edit">
      <div v-if="editing" class="saving-information">{{ $t('orderSiteWarning') }}</div>

      <span v-if="editing" class="save-button">{{ $t('save') }}</span>
      <span v-else>{{ $t('editOrder') }}</span>
    </div>
  </AsideGabarit>
</template>

<script>
import Draggable from 'vuedraggable';
import { mapGetters } from 'vuex';

import AsideGabarit from './AsideGabarit';
import ToggleFilter from './ToggleFilter';
import assetProvider from '@/pure/assetProvider';
import { useSignalR } from '@dreamonkey/vue-signalr';

export default {
  components: {
    Draggable,
    AsideGabarit,
    ToggleFilter,
  },

  props: ['action'],

  data() {
    return {
      editing: false,
      saving: false,
      sortedSites: [],
    };
  },

  computed: {
    ...mapGetters({
      isDesktop: 'isDesktop',
      sites: 'sites',
      globalFilters: 'globalFilters',
      study: 'study',
      userInfo: 'userInfo',
    }),

    targetSites() {
      return this.sites.filter(site => site.type === 'Target').sort((a, b) => a.index - b.index);
    },

    siteFilter() {
      return this.globalFilters.targetSites;
    },
  },

  watch: {
    targetSites: {
      handler() {
        this.sortedSites = this.targetSites;
      },

      deep: true,
    },
  },

  created() {
    this.$socket = useSignalR();
  },

  mounted() {
    this.sortedSites = this.targetSites.map((e, i) => {
      e.order = i;
      return e;
    });
  },

  methods: {
    currentPin(index) {
      return assetProvider[`poi${index + 1}`];
    },

    edit() {
      if (this.editing) {
        this.saving = true;

        this.sortedSites = this.sortedSites.map((site, index) => {
          site.order = index;
          return site;
        });

        this.$store.dispatch('updateTargetSitesOrder', this.sortedSites).then(() => {
          this.saving = false;

          if (!this.isDesktop || this.$route.name === 'Controller') {
            this.$socket.invoke('action', { action: 'tvSites', parameters: this.$store.getters.screen().sites });
          }
        });
      }

      this.editing = !this.editing;
    },

    toggleFilterSites(id) {
      if (this.isSiteActive(id)) {
        if (this.siteFilter.length > 1) {
          const idx = this.globalFilters.targetSites.indexOf(id);
          this.globalFilters.targetSites.splice(idx, 1);
        }
      } else {
        this.globalFilters.targetSites.push(id);
      }

      this.$store
        .dispatch('updateGlobalFilters', {
          key: 'targetSites',
          value: this.globalFilters.targetSites,
        })
        .then(() => {
          if (!this.isDesktop || this.$route.name === 'Controller') {
            if (this.study && this.study.collaborators_total > 2000) {
              const id = this.study.id || this.$route.params.studyId;
              this.$store
                .dispatch('uploadRawData', {
                  id: id,
                  content: this.$store.getters.screen(),
                })
                .then(result => {
                  this.$socket.invoke('action', {
                    action: 'tvBlob',
                    parameters: result,
                  });
                });
            } else {
              this.$socket.invoke('action', {
                action: 'tvData',
                parameters: this.$store.getters.screen(),
              });
            }
          }
        });
    },

    isSiteActive(id) {
      return this.siteFilter.find(item => item === id);
    },
  },
};
</script>

<style lang="scss" scoped>
@use '@/styles/colors.scss';

.site-container {
  .site {
    position: relative;

    .icon-pin {
      height: 40px;
      width: auto;
      margin-left: -0.25em;
      margin-right: 0.5em;
    }

    .handle {
      position: absolute;
      display: flex;
      align-items: center;
      justify-content: center;
      height: 100%;
      width: 48px;
      margin: 0 -16px 0 -18px;
      padding: 0 16px 0 0;
      color: rgba(colors.$concrete-color, 0.8);
      transition: 0.2s ease-in-out;
      cursor: grab;

      &:active {
        color: colors.$concrete-color;
        cursor: grabbing;
      }
    }
  }
}

.edit-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 48px;
  padding: 8px 12px 8px 0;
  box-sizing: border-box;

  .saving-information {
    background: colors.$beige-color;
    color: colors.$dark-grey-color;
    border-radius: 8px;
    padding: 12px;
    font-size: 12px;
  }

  span {
    cursor: pointer;
  }

  .save-button {
    margin: 8px 0;
    padding: 0 30px;
    display: flex;
    align-items: center;
    box-sizing: border-box;
    height: 38px;
    background: colors.$main-color;
    border: solid 2px colors.$main-color;
    border-radius: 38px;
    font-weight: 600;
    text-align: center;
    color: colors.$white-color;
    cursor: pointer;
    transition: all 0.3s ease;
    font-family: 'Source Sans Pro', sans-serif;
    font-size: 14px;

    &:hover {
      background: colors.$white-color;
      color: colors.$main-color;
    }
  }
}
</style>
